import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./C5CargoAndPassengers.scss"

import { Button, Label, TextArea } from "abs-ui-components";

import { ALLOWED_SHIP_TYPES, AUTOSAVE_SUCCESS_MESSAGE, c41Fields } from "../../Constants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import AutoSave from "../../../../../common/AutoSave/AutoSave";

const C5CargoAndPassengers = forwardRef(({ c51CargoAndPassenger, vesselMstId, c52CargoDensity, refetchMonitoringData, b1shipdetails, dropdownData }, ref) => {
    const { control, getValues, reset, setValue, formState } = useFormContext();
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    const { dirtyFields } = formState;

    const isC51Dirty = !!(dirtyFields?.c51 && Object.keys(dirtyFields.c51).length > 0);
    const isC52Dirty = !!(dirtyFields?.c52 && Object.keys(dirtyFields.c52).length > 0);

    useEffect(() => {
        if (c51CargoAndPassenger) {
            reset(prev => ({ ...prev, c51: c51CargoAndPassenger }));
        }
        if (c52CargoDensity) {
            reset(prev => ({ ...prev, c52: c52CargoDensity }));
        }
    }, [c51CargoAndPassenger, c52CargoDensity, reset]);

    const isC52Enabled = useMemo(() => {
        if (!b1shipdetails?.shipType || !dropdownData?.shipTypes?.length) {
            return false;
        }
        const match = dropdownData.shipTypes.find(
            (item) => item.code === b1shipdetails.shipType
        );
        return match ? ALLOWED_SHIP_TYPES.includes(match.value) : false;
    }, [b1shipdetails, dropdownData]);


    const unitOfCargo = useMemo(() => {
        if (!b1shipdetails?.shipType) return "";
        const match = dropdownData?.shipTypes?.find(
            (s) => s.code === b1shipdetails.shipType
        );
        return match ? match.unit : "";
    }, [dropdownData, b1shipdetails]);

    useEffect(() => {
        setValue("c51.unitOfCargo", unitOfCargo, {
            shouldValidate: false,
            shouldDirty: false,
        });
    }, [unitOfCargo, setValue]);

    const c51Fields = useMemo(() => {
        const base = c41Fields.filter(f => f.name !== "dataSources");
        const result = [];
        base.forEach(f => {
            switch (f.name) {
                case "descriptionOfProcedure":
                    result.push({
                        ...f,
                        label:
                            "Description of procedure (including recording and determining the amount of cargo carried and/or the number of passengers and the use of default values for the mass of cargo units, if applicable. A brief description of the procedure can be provided if already existing outside the monitoring plan)",
                    });
                    result.push({
                        label: "Unit of cargo/passengers",
                        name: "unitOfCargo",
                    });
                    break;
                case "responsiblePersonOrPosition":
                    result.push(f);
                    result.push({
                        label: "Formulae and data sources",
                        name: "formulaeAndDataSources",
                        maxLength: 1000,
                    });
                    break;
                default:
                    result.push(f);
            }
        });
        return result;
    }, []);

    const c52Fields = useMemo(() => {
        const base = c41Fields.filter(f => f.name !== "dataSources");
        const result = [];
        base.forEach(f => {
            switch (f.name) {
                case "descriptionOfProcedure":
                    result.push({
                        ...f,
                        label:
                            "Description of procedures (including recording and managing cargo density information. A brief description of the procedure can be provided if already existing outside the monitoring plan)",
                    });
                    break;
                case "responsiblePersonOrPosition":
                    result.push(f);
                    result.push({
                        label: "Formulae and data sources",
                        name: "formulaeAndDataSources",
                        maxLength: 1000,
                    });
                    break;
                default:
                    result.push(f);
            }
        });
        return result;
    }, []);


    AutoSave(ref, [], async () => {
        if (isC51Dirty) await handleSave51(true);
        if (isC52Dirty) await handleSave52(true);
    });

    const handleSave51 = async (isAuto = false) => {
        const formValues = getValues("c51");
        const payload = {
            ...c51Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const C5CargoAndPassengersC41 = await callApi({
                method: "POST",
                url: API_ROUTES.C51(vesselMstId),
                data: payload,
            });
            if (C5CargoAndPassengersC41?.isSuccess) {
                emitToasterMsg("success", "Table C.5.1", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c51: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.5.1", C5CargoAndPassengersC41?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.5.1", error?.errorMessage);
        }
    };

    const handleSave52 = async (isAuto = false) => {
        const formValues = getValues("c52");
        const payload = {
            ...c52Fields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const C5CargoAndPassengersC52 = await callApi({
                method: "POST",
                url: API_ROUTES.C52(vesselMstId),
                data: payload,
            });
            if (C5CargoAndPassengersC52?.isSuccess) {
                emitToasterMsg("success", "Table C.5.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, c52: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table C.5.2", C5CargoAndPassengersC52?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.5.2", error?.errorMessage);
        }
    };

    return (
        <>
            <div className="c51-section-form mt-4">
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Recording and determining the amount of cargo carried and/or the number of passengers</h6>
                    </MDBCol>
                </MDBRow>

                {c51Fields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c51.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c51_${name}`}
                                        className="textarea-resize"
                                        value={
                                            name === "unitOfCargo"
                                                ? unitOfCargo
                                                : field.value || ""
                                        }
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                        readOnly={name === "unitOfCargo"}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}

                <div className="text-center">
                    <Button id="c51_save_btn"
                        disabled={!isC51Dirty}
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave51(false);
                        }}
                    >Save</Button>
                </div>
            </div>

            <div className={`mp-headers ${isC52Enabled ? "text-header" : "text-opacity"}`}>
                C5.2 Procedures for determining and recording the average density of the cargoes transported (voluntary monitoring for chemical tankers, bulk carriers and combination carriers):
            </div>

            <div className={`c52-section-form mt-5 ${!isC52Enabled ? "disabled" : ""}`}>
                <MDBRow className="mb-2">
                    <MDBCol md="4">
                        <h6 className="titles-font">Title of procedure</h6>
                    </MDBCol>
                    <MDBCol md="6">
                        <h6 className="titles-font">
                            Determining and recording the average density of the cargoes transported</h6>
                    </MDBCol>
                </MDBRow>

                {c52Fields.map(({ label, name, maxLength }, index) => (
                    <MDBRow className="align-items-center mb-3" key={index}>
                        <MDBCol md="4">
                            <Label name={label} bold />
                        </MDBCol>
                        <MDBCol md="4">
                            <Controller
                                name={`c52.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`c52_${name}`}
                                        className="textarea-resize"
                                        value={field?.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                        readOnly={!isC52Enabled}
                                    />
                                )}
                            />
                        </MDBCol>
                    </MDBRow>
                ))}

                <div className="text-center">
                    <Button id="c52_save_btn"
                        onClick={async () => {
                            manualSaveTriggered.current = true;
                            await handleSave52(false);
                        }}
                        disabled={!isC52Enabled || !isC52Dirty}>Save</Button>
                </div>
            </div>
        </>
    );
});

export default C5CargoAndPassengers;